import { cn } from "@/modules/lib/utils"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cva, type VariantProps } from "class-variance-authority"

function Tabs({ className, orientation = "horizontal", ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn("group/tabs flex gap-2 data-[orientation=horizontal]:flex-col", className)}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit group-data-[orientation=horizontal]/tabs:h-13 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col",
  {
    variants: {
      variant: {
        default: "mx-auto w-[93%] border-b-1 border-borderDefault",
        roundedButton: "group-data-[orientation=horizontal]/tabs:h-fit",
        profile: "mx-auto w-[93%] flex-wrap",
        program:
          "absolute -top-4 left-1/2 w-[95%] -translate-x-1/2 items-center justify-center rounded-4xl bg-surface-foreground p-[3px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function TabsList({
  className,
  variant = "default",
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
}

const tabsTriggerVariants = cva(
  "inline-flex cursor-pointer items-center whitespace-nowrap text-textDefault-body disabled:pointer-events-none disabled:opacity-50 data-active:text-base [&_svg]:pointer-events-none [&_svg]:mr-1.5 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "h-full p-4 label-large text-sm text-textDefault-body/50 hover:border-b-1 data-active:border-b-2 data-active:border-neutral-900 data-active:text-sm data-active:text-textDefault-body",
        roundedButton:
          "mr-2 rounded-full border-1 border-solid border-borderDefault px-3 py-2 text-sm text-textDefault-body data-active:bg-neutral-200 data-active:text-sm data-active:font-normal",
        profile:
          "text-md h-full border-b-1 border-borderDefault p-4 font-semibold text-textDefault-body/50 hover:border-b-2 hover:border-neutral-500 data-active:border-b-2 data-active:border-neutral-900 data-active:text-textDefault-body",
        program:
          "h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-4xl border border-transparent px-2 py-1 text-sm font-medium data-active:bg-neutral-900 data-active:text-textOnColor-heading",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function TabsTrigger({
  variant,
  className,
  ...props
}: TabsPrimitive.Tab.Props & VariantProps<typeof tabsTriggerVariants>) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant, className }))}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("flex-1 text-sm outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
